'use client';

// Shared "News" tab for the unified SidePanel — the live News Room tape
// (Alpaca/Benzinga), with an All / active-symbol scope toggle. Clicking a
// tagged ticker calls onSelectSymbol (switch chart in terminal / navigate on
// the stock page). Per Dan: the News tab is *only* the live News Room (the old
// per-symbol Yahoo RSS news tab is dropped).

import React, { useEffect, useState } from 'react';
import { getLiveNews, type LiveNewsItem } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import TickerBadge, { TICKER_CHIP_TINT } from '@/components/ticker/TickerBadge';
import { isEnglishHeadline } from '@/lib/newsLanguageFilter';

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function PanelNews({ symbol, onSelectSymbol, tickerMode = 'select' }: {
  symbol: string;
  onSelectSymbol: (s: string) => void;
  /** 'select' (terminal) → tag click loads the symbol; 'popup' (global) → the
   *  app-standard TickerBadge popup, matching the stock pages. */
  tickerMode?: 'select' | 'popup';
}) {
  const [items, setItems] = useState<LiveNewsItem[] | null>(null);
  const [scope, setScope] = useState<'all' | 'symbol'>('all');
  const hasSymbol = !!symbol;

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    const load = async () => {
      try {
        const res = await getLiveNews(scope === 'symbol' && hasSymbol ? { symbols: [symbol], limit: 60 } : { limit: 60 });
        if (!cancelled) setItems(res.items.filter((it) => isEnglishHeadline(it.headline, it.url)));
      } catch { if (!cancelled) setItems([]); }
    };
    load();
    const id = window.setInterval(() => { if (!document.hidden) load(); }, 10_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [scope, symbol, hasSymbol]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-default px-3 py-2">
        <span className="inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE
        </span>
        {hasSymbol && (
          <div className="ml-auto flex items-center gap-1">
            {(['all', 'symbol'] as const).map((s) => (
              <button key={s} onClick={() => setScope(s)}
                className={cn('rounded px-2 py-0.5 text-[10px] font-medium',
                  scope === s ? 'bg-sky-500/20 text-sky-200' : 'text-slate-500 hover:text-slate-300')}>
                {s === 'all' ? 'All' : symbol}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {items === null ? (
          <div className="px-3 py-3 text-xs text-fg-subtle">Loading live news…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-fg-subtle">
            {scope === 'symbol' && symbol ? `No live news for ${symbol} yet.` : 'No live news yet — the tape fills in as headlines break.'}
          </div>
        ) : (
          <div className="divide-y divide-muted">
            {items.map((it) => (
              <div key={it.id} className="px-3 py-1.5 hover:bg-white/5">
                <div className="flex items-start gap-2">
                  <a href={it.url || '#'} target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-xs leading-snug text-slate-200 hover:text-white no-underline">
                    {it.headline}
                  </a>
                  <span className="flex-shrink-0 font-mono text-[10px] text-slate-500">{timeAgo(it.createdAt)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1">
                  {it.symbols.slice(0, 6).map((sym) => (
                    tickerMode === 'popup'
                      ? <TickerBadge key={sym} ticker={sym} />
                      : <button key={sym} type="button" onClick={() => onSelectSymbol(sym)} title={`Load ${sym}`}
                          className={cn('rounded px-1 py-0.5 font-mono text-[10px] font-semibold transition-colors',
                            sym === symbol ? 'bg-sky-500/25 text-sky-200' : `${TICKER_CHIP_TINT} hover:bg-sky-500/20`)}>
                          {sym}
                        </button>
                  ))}
                  {it.source && <span className="text-[10px] text-slate-600">{it.source}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
